import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Client } from '@/api/clients/clientsApi.types';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { ListPagination } from '@/components/admin/shared/ListPagination';
import { ListSearchInput } from '@/components/admin/shared/ListSearchInput';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ADMIN_LIST_PAGE_SIZE, SEARCH_DEBOUNCE_MILLISECONDS } from '@/constants/adminPagination';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useClientsQuery } from '@/hooks/useClientsQuery';
import { buildNameInitials } from '@/lib/nameInitials';
import { cn } from '@/lib/utils';
import { formatPhoneNumberForDisplay } from '@/utils/phoneNumbers';
import { ClientFormModal } from './ClientFormModal';
import { PortalStatusPill } from './PortalStatusPill';

const CLIENT_STATUS = { active: 'active', archived: 'archived' } as const;

export function ClientsPage() {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MILLISECONDS);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>(CLIENT_STATUS.active);
  const [isFormOpen, setIsFormOpen] = useState(false);

  useEffect(
    function resetPageOnFilterChange() {
      setPage(1);
    },
    [debouncedSearch, statusFilter]
  );

  const clientsQuery = useClientsQuery(page, debouncedSearch, statusFilter);
  const result = clientsQuery.data;
  const hasSearch = debouncedSearch.trim().length > 0;

  return (
    <div className="p-6 md:p-8">
      <AdminPageHeader
        title={t('list.title')}
        subtitle={t('list.subtitle')}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={function goToImport() {
                navigate(APPLICATION_ROUTES.workspaceClientImport);
              }}
            >
              <Icon name="Upload" />
              {t('list.import')}
            </Button>
            <Button
              onClick={function openForm() {
                setIsFormOpen(true);
              }}
            >
              <Icon name="Plus" />
              {t('list.add')}
            </Button>
          </div>
        }
      />

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <div className="flex-1">
          <ListSearchInput
            value={searchInput}
            onChange={setSearchInput}
            placeholder={t('list.search')}
          />
        </div>
        <div className="inline-flex bg-zoowhite border border-zooborder rounded-xl p-1 self-start">
          {[CLIENT_STATUS.active, CLIENT_STATUS.archived].map(function renderTab(value) {
            const isOn = statusFilter === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={isOn}
                onClick={function selectStatus() {
                  setStatusFilter(value);
                }}
                className={cn(
                  'px-4 py-2 rounded-lg text-xs font-bold transition-colors',
                  isOn ? 'bg-zooprimary text-zoowhite' : 'text-zoomuted'
                )}
              >
                {value === CLIENT_STATUS.active ? t('list.active') : t('list.archived')}
              </button>
            );
          })}
        </div>
      </div>

      {clientsQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : clientsQuery.isError === true || result === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            clientsQuery.refetch();
          }}
        />
      ) : (
        <>
          <ClientsTable
            clients={result.items}
            hasSearch={hasSearch}
            isArchived={statusFilter === CLIENT_STATUS.archived}
            onOpen={function openClient(clientId) {
              navigate('/workspace/clients/' + clientId);
            }}
          />
          <ListPagination
            page={page}
            pageSize={ADMIN_LIST_PAGE_SIZE}
            totalItems={result.totalItems}
            onPageChange={setPage}
          />
        </>
      )}

      {isFormOpen === true ? (
        <ClientFormModal
          client={null}
          onClose={function closeForm() {
            setIsFormOpen(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface ClientsTableProps {
  clients: Client[];
  hasSearch: boolean;
  isArchived: boolean;
  onOpen: (clientId: string) => void;
}

function ClientsTable({ clients, hasSearch, isArchived, onOpen }: ClientsTableProps) {
  const { t } = useTranslation('clients');

  if (clients.length === 0) {
    const message = hasSearch
      ? t('list.noResults')
      : isArchived
        ? t('list.emptyArchived')
        : t('list.empty');
    return (
      <Card padding="lg" className="text-center text-sm text-zoomuted">
        {message}
      </Card>
    );
  }

  return (
    <Card padding="none" className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-zooborder">
            <Th>{t('list.columnClient')}</Th>
            <Th>{t('list.columnPets')}</Th>
            <Th className="hidden md:table-cell">{t('list.columnPhone')}</Th>
            <Th className="hidden lg:table-cell">{t('form.email')}</Th>
            <Th className="hidden sm:table-cell">{t('list.columnPortal')}</Th>
            <Th className="text-right">{t('list.columnActions')}</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zooborder">
          {clients.map(function renderClient(client) {
            return (
              <tr
                key={client.id}
                onClick={function open() {
                  onOpen(client.id);
                }}
                className="cursor-pointer hover:bg-zoovpalelavender hover:bg-opacity-30 transition-colors"
              >
                <td className="px-4 sm:px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-zoovpalelavender text-zooprimary flex items-center justify-center font-bold text-xs shrink-0">
                      {buildNameInitials(client.fullName)}
                    </span>
                    <button
                      type="button"
                      onClick={function openFromName(clickEvent) {
                        clickEvent.stopPropagation();
                        onOpen(client.id);
                      }}
                      className="text-left text-sm font-bold text-zooindigo truncate hover:text-zooprimary hover:underline"
                    >
                      {client.fullName}
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3.5 text-sm text-zoomuted">{client.petsCount}</td>
                <td className="px-4 py-3.5 text-sm text-zoomuted hidden md:table-cell">
                  {client.phone === null ? '—' : formatPhoneNumberForDisplay(client.phone)}
                </td>
                <td className="px-4 py-3.5 text-sm text-zoomuted hidden lg:table-cell truncate">
                  {client.email === null ? '—' : client.email}
                </td>
                <td className="px-4 py-3.5 hidden sm:table-cell">
                  <PortalStatusPill status={client.portalStatus} />
                </td>
                <td className="px-4 sm:px-5 py-3.5 text-right">
                  <Icon name="ChevronRight" className="text-zoomuted inline-block" />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={
        'px-4 sm:px-5 py-3 text-[10.5px] font-bold uppercase tracking-wider text-zoomuted ' +
        (className === undefined ? '' : className)
      }
    >
      {children}
    </th>
  );
}
